import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';

export type EvaluationSummary = {
  protocolCount: number;
  elementCount: number;
  protocolNames: string[];
};

type EvaluationTypeCardProps = {
  title: string;
  description: string;
  summary: EvaluationSummary;
  icon: React.ReactNode;
  accentColor: string;
  emptyMessage: string;
  configureLabel: string;
  addCriteriaLabel: string;
  protocolsLabel: string;
  elementsLabel: string;
  includedLabel: string;
  moreLabel: string;
  onClick: () => void;
};

const EvaluationTypeCard: React.FC<EvaluationTypeCardProps> = ({
  title,
  description,
  summary,
  icon,
  accentColor,
  emptyMessage,
  configureLabel,
  addCriteriaLabel,
  protocolsLabel,
  elementsLabel,
  includedLabel,
  moreLabel,
  onClick
}) => {
  const { protocolCount, elementCount, protocolNames } = summary;
  const isEmpty = protocolCount === 0 && elementCount === 0;

  const previewText = useMemo(() => {
    if (protocolNames.length === 0) {
      return '';
    }
    const preview = protocolNames.slice(0, 3);
    const remaining = protocolNames.length - preview.length;
    if (remaining <= 0) {
      return preview.join(', ');
    }
    return `${preview.join(', ')} +${remaining} ${moreLabel}`;
  }, [protocolNames, moreLabel]);

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 200,
        borderTop: '4px solid',
        borderTopColor: accentColor,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
    >
      <CardActionArea
        onClick={onClick}
        component="div"
        role="button"
        tabIndex={0}
        sx={{
          height: '100%',
          p: 2,
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box display="flex" width="100%" mb={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mr: 2,
              bgcolor: alpha(accentColor, isEmpty ? 0.25 : 0.9),
              color: isEmpty ? 'text.secondary' : 'common.white'
            }}
          >
            {icon}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>

        <Box width="100%" flexGrow={1}>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
            <Chip
              label={`${protocolCount} ${protocolsLabel}`}
              size="small"
              color={protocolCount > 0 ? 'primary' : 'default'}
              variant={protocolCount > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              label={`${elementCount} ${elementsLabel}`}
              size="small"
              variant="outlined"
            />
          </Stack>

          {isEmpty ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {emptyMessage}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {includedLabel}: {previewText}
            </Typography>
          )}
        </Box>

        <Box width="100%" display="flex" justifyContent="flex-end" mt={2}>
          <Button size="small" variant={isEmpty ? 'outlined' : 'text'}>
            {isEmpty ? addCriteriaLabel : configureLabel}
          </Button>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default EvaluationTypeCard;
